import { useAuth } from '../../components/provider/OIDCProvider'
import useAdvisorCanViewOwnPlan from './useAdvisorCanViewOwnPlan'

/**
 * Centralizes the "a dual-role user in advising view may not open their own
 * student record" rule (lifted when the institution enables `advisorViewOwnPlan`).
 *
 * This is a UX-scoping control, not a security boundary: the OIDC token is
 * unchanged on every request, so this only gates the client. The protected
 * resource is the user's own data, already reachable via the student persona.
 */
const useAdvisorOwnRecordGuard = () => {
  const { user, isDualRole, activeRole } = useAuth() || {}
  const advisorCanViewOwnPlan = useAdvisorCanViewOwnPlan()

  const isAdvisingWithoutOwnPlanAccess =
    !!isDualRole && activeRole === 'advisor' && !advisorCanViewOwnPlan

  // The token carries two distinct SIS identifiers and a StudentProfile search
  // result may key on either, so the pre-mutation check compares against both.
  const ownSisIds = [
    user?.['https://api.student.civitaslearning.com/studentId'],
    user?.['https://api.student.civitaslearning.com/externalId']
  ].filter((id): id is string => !!id)

  return {
    /** True in the advisor persona at an institution that disallows own-plan access. */
    isAdvisingWithoutOwnPlanAccess,
    /** Block by the authoritative app-user id (accepts the string ids GraphQL returns). */
    isOwnRecord: (appUserId?: number | string | null) =>
      isAdvisingWithoutOwnPlanAccess && appUserId != null && Number(appUserId) === user?.appUserId,
    /** Block by SIS external id — used pre-mutation, when the app-user id isn't known yet. */
    isOwnSisId: (externalId?: string | null) =>
      isAdvisingWithoutOwnPlanAccess && !!externalId && ownSisIds.includes(externalId)
  }
}

export default useAdvisorOwnRecordGuard
